import bootstrapModalUtils from './bootstrap-modal-utils';
import forms from './forms';
import icons from './site-icons';
import handlebarsUtils from './handlebars-utils';
import { diffWords, diffSentences, Change } from 'diff';
import levenshtein from 'fast-levenshtein';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import dateUtils from './date-utils';
import { SideNavBuilder } from './side-nav-builder';

dayjs.extend(relativeTime);

export class EntityHistoryItem {
	public timestamp: dayjs.Dayjs;
	public userName: string;
	public actionDisplay: string;
	public changeReason: string;
	public comments: string;
	public cancelReason: string;
	public milestoneId: number;
	public sourceDisplay: string;

	constructor (rowData: any) {
		this.timestamp = dayjs(rowData.timestamp);
		this.userName = rowData.user;
		this.actionDisplay = rowData.actionDisplay;
		this.changeReason = rowData.changeReason || '';
		this.comments = rowData.comments || '';
		this.cancelReason = rowData.cancelReason || '';
		this.milestoneId = rowData.milestoneId;
		this.sourceDisplay = rowData.sourceDisplay || '';
	}

	public itemId (): string {
		return `history-item-${this.milestoneId}`;
	}

	public relativeTimestamp (): string {
		return this.timestamp.fromNow();
	}

	public formattedTimestamp (): string {
		return dateUtils.displayDateTime(this.timestamp);
	}
}

export class EntityDetails {
	public entityHistory: EntityHistoryItem[] = [];

	constructor (public reviewEnabledStorageKey: string) {
		this.entityHistory = (window as any).historyItems.map((d: any) => new EntityHistoryItem(d));
	}

	public init () {
		icons.registerSiteIcons();

		const historyDetailsTemplate = handlebarsUtils.compileTemplateFrom('history-details-template');
		$('#entity-history-details').html(historyDetailsTemplate(this));

		const approveAndNextParameters = {
			selectorParameters: (document.getElementById('SelectorParameters') as HTMLInputElement).value
		};

		const modalContainerTarget = 'detailsModalContainer';
		bootstrapModalUtils.configureConfirmActionLink($('.approve-action'), {}, modalContainerTarget);

		bootstrapModalUtils.configureConfirmActionLink($('.approve-and-review-next-action'),
			approveAndNextParameters, modalContainerTarget);

		const buildRequestChangesCommand = () => {
			return {
				changesRequested: forms.getInputValue('#ChangesRequested')
			};
		};
		const buildDateChangesCommand = () => {
			return {
				statusDate: forms.getInputValue('#StatusDate')
			};
		};

		bootstrapModalUtils.configureActionLinkFromTemplate($('.request-changes-action'),
			null, modalContainerTarget, buildRequestChangesCommand);

		bootstrapModalUtils.configureActionLinkFromTemplate($('.request-changes-and-review-next-action'),
			approveAndNextParameters, modalContainerTarget, buildRequestChangesCommand);

		const context = {
			entityHistory: this.entityHistory
		};
		bootstrapModalUtils.configureActionLinkFromTemplate($('.request-change-dates-action'),
			context, modalContainerTarget, buildDateChangesCommand);

		this.initDifferences();
		this.buildSideNav();
	}

	public hasHistory (): boolean {
		return this.entityHistory.length > 0;
	}

	private buildSideNav () {
		const sideNavBuilder = new SideNavBuilder();
		sideNavBuilder.render('.entity-data-container fieldset', '#side-nav-content');
	}

	private displayDifference (element: Element, enable: boolean) {
		// newValue contains the current value
		const newValue = element.querySelector('.new-value')!.innerHTML;
		// oldValue contains the previous value
		const oldValue = element.querySelector('.old-value')!.innerHTML;
		// valueCompare contains the rendered diff
		let displayValueCompare = element.querySelector('.display-value-compare');
		// value contains the original diff after valueCompare has been rendered
		let displayValue = element.querySelector('.display-value');

		if (!displayValue) {
			// text node contains the original display output
			const children = Array.from(element.childNodes);
			const textNode = children.find((node) => node.nodeType === Node.TEXT_NODE);
			const childNode = children
				.filter((node) => node.nodeType === Node.ELEMENT_NODE)
				.map((node) => node as HTMLElement)
				.find((node) => !node.classList.contains('new-value') && !node.classList.contains('old-value'));

			const output = document.createElement('div');
			output.classList.add('display-value');
			output.classList.add('d-none');

			if (textNode) {
				if (textNode.nodeValue) {
					output.innerHTML = textNode.nodeValue;
				}

				element.append(output);
				textNode.remove();
			} else if (childNode) {
				output.appendChild(childNode);
				element.append(output);
			} else {
				throw Error('Value comparison not available because the field-value child type is unknown.');
			}

			displayValue = output;
		}

		if (!displayValueCompare) {
			const output = document.createElement('div');
			output.classList.add('display-value-compare');
			output.classList.add('d-none');

			const distance = levenshtein.get(newValue, oldValue);
			let diff: Change[];

			if (distance > 5 || Math.max(oldValue.length, newValue.length) <= 50) {
				diff = diffSentences(oldValue, newValue);
			} else {
				diff = diffWords(oldValue, newValue);
			}

			diff.forEach((part) => {
				const span = document.createElement('span');
				if (part.added) {
					span.classList.add('text-added');
				} else if (part.removed) {
					span.classList.add('text-removed');
				}

				// span.appendChild(document.createTextNode(part.value));
				span.innerHTML = part.value;
				output.appendChild(span);
			});
			element.appendChild(output);
			displayValueCompare = output;
		}

		if (!enable) {
			displayValue!.classList.remove('d-none');
			displayValueCompare!.classList.add('d-none');
		} else {
			displayValue!.classList.add('d-none');
			displayValueCompare!.classList.remove('d-none');
		}
	}

	private toggleChanges (showChangesButton: HTMLInputElement, state: boolean) {
		showChangesButton.classList.toggle('active', state);
		showChangesButton.blur();

		document.querySelectorAll('.changed-value').forEach((element) => {
			this.displayDifference(element, state);
		});

		return state;
	}

	private initDifferences () {
		const showChangesButton: HTMLInputElement | null = document.querySelector('.show-changes') as HTMLInputElement;

		if (showChangesButton) {
			const storageKey = this.reviewEnabledStorageKey;
			const initial = localStorage.getItem(storageKey) === 'true';

			showChangesButton.addEventListener('click', (_) => {
				const currentState = localStorage.getItem(storageKey) === 'true';
				const newState = this.toggleChanges(showChangesButton, !currentState);

				localStorage.setItem(storageKey, newState.toString());
			});

			this.toggleChanges(showChangesButton, initial);
		}
	}
}
